import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { salesOrdersApi as SO, SO_STATUS_STYLE, PRIORITY_STYLE, inr } from "@/lib/salesOrders";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, CheckCircle2, Factory, XCircle, GitPullRequestArrow } from "lucide-react";

const FIELD_LABELS = { priority: "Priority", delivery_date: "Delivery date", shipping_method: "Shipping method",
  payment_terms: "Payment terms", remarks: "Remarks", production_notes: "Production notes", customer_instructions: "Customer instructions" };

export default function SalesOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [so, setSo] = useState(null);
  const [meta, setMeta] = useState({ priorities: [], shipping_methods: [] });
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [revOpen, setRevOpen] = useState(false);
  const [rev, setRev] = useState({ field: "delivery_date", value: "", reason: "" });
  const canEdit = can("sales_orders", "edit");
  const canApprove = can("sales_orders", "approve");

  const load = async () => {
    setLoading(true);
    try { setSo(await SO.get(id)); } catch { toast.error("Not found"); navigate("/sales-orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); SO.meta().then(setMeta); /* eslint-disable-next-line */ }, [id]);

  const move = async (status, note = null) => {
    try { setSo(await SO.setStatus(id, status, note)); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const submitCancel = async () => {
    if (!cancelReason) { toast.error("Enter a reason"); return; }
    await move("Cancelled", cancelReason); setCancelOpen(false); setCancelReason("");
  };
  const submitRevision = async () => {
    if (!rev.value || !rev.reason) { toast.error("Enter the new value and a reason"); return; }
    try { setSo(await SO.requestRevision(id, { [rev.field]: rev.value }, rev.reason)); toast.success("Revision requested"); setRevOpen(false); setRev({ field: "delivery_date", value: "", reason: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const decide = async (rid, decision) => {
    try { setSo(await SO.decideRevision(id, rid, decision)); toast.success(decision === "approve" ? "Revision approved" : "Revision rejected"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading || !so) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  const t = so.totals || {}; const c = so.customer || {};
  const pendingRev = (so.revisions || []).find((r) => r.status === "Pending");
  const revisable = ["Confirmed", "Production Planning", "In Production"].includes(so.status);

  return (
    <div className="space-y-6" data-testid="sales-order-detail-page">
      <button onClick={() => navigate("/sales-orders")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to sales orders</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{so.so_no}</h2>
            <Badge className={`hover:bg-inherit ${SO_STATUS_STYLE[so.status]}`} data-testid="so-status-badge">{so.status}</Badge>
            <Badge className={`hover:bg-inherit ${PRIORITY_STYLE[so.priority]}`}>{so.priority}</Badge>
            {so.revision_no > 0 && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Rev {so.revision_no}</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500">{c.company_name} · from {so.quote_no} · created {new Date(so.created_at).toLocaleDateString()}</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {so.status === "Draft" && <Button variant="outline" onClick={() => navigate(`/sales-orders/${id}/edit`)} data-testid="edit-so-btn"><Pencil className="mr-2 h-4 w-4" />Edit</Button>}
            {so.status === "Draft" && <Button className="bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => move("Confirmed")} data-testid="confirm-so-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Confirm order</Button>}
            {so.status === "Confirmed" && <Button variant="outline" onClick={() => move("Production Planning")} data-testid="to-planning-btn"><Factory className="mr-2 h-4 w-4" />Send to Planning</Button>}
            {so.status === "Production Planning" && <Button variant="outline" onClick={() => move("In Production")} data-testid="to-production-btn"><Factory className="mr-2 h-4 w-4" />Start Production</Button>}
            {so.status === "In Production" && <Button variant="outline" onClick={() => move("Completed")} data-testid="complete-so-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Mark Completed</Button>}
            {so.status === "Partially Dispatched" && <Button variant="outline" onClick={() => move("Completed")} data-testid="complete-so-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Mark Completed</Button>}
            {so.status === "Completed" && <Button variant="outline" onClick={() => move("Closed")} data-testid="close-so-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Close order</Button>}
            {revisable && !pendingRev && <Button variant="outline" onClick={() => setRevOpen(true)} data-testid="request-revision-btn"><GitPullRequestArrow className="mr-2 h-4 w-4" />Request revision</Button>}
            {["Draft", "Confirmed", "Production Planning", "In Production"].includes(so.status) && <Button variant="outline" className="border-rose-300 text-rose-700" onClick={() => setCancelOpen(true)} data-testid="cancel-so-btn"><XCircle className="mr-2 h-4 w-4" />Cancel</Button>}
          </div>
        )}
      </div>

      {pendingRev && (
        <Card className="border-amber-200 bg-amber-50 p-4" data-testid="pending-revision-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <p className="font-medium text-amber-800">Revision pending approval</p>
              <p className="text-amber-700">{Object.entries(pendingRev.changes).map(([k, v]) => `${FIELD_LABELS[k] || k} → ${v}`).join(", ")} · {pendingRev.reason} <span className="text-amber-500">(by {pendingRev.requested_by})</span></p>
            </div>
            {canApprove && (
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => decide(pendingRev.id, "approve")} data-testid="approve-revision-btn">Approve</Button>
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={() => decide(pendingRev.id, "reject")} data-testid="reject-revision-btn">Reject</Button>
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-slate-900">Line items (inherited)</h3>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Fabric / Print</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Specs</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Qty</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Rate</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(so.items || []).map((it, i) => (
                  <TableRow key={it.id || i} data-testid={`so-line-${i}`}>
                    <TableCell className="font-medium text-slate-800">{it.fabric_type}<span className="block text-xs text-slate-400">{it.print_type}</span></TableCell>
                    <TableCell className="text-slate-600">{[it.gsm && `${it.gsm} GSM`, it.width].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="text-slate-600">{it.quantity} {it.unit}</TableCell>
                    <TableCell className="tabular-nums text-slate-600">{inr(it.rate)}</TableCell>
                    <TableCell className="tabular-nums font-medium text-slate-800">{inr(it.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between text-slate-600"><span>Taxable value</span><span className="tabular-nums">{inr(t.taxable_value)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Tax</span><span className="tabular-nums">{inr(t.total_tax)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold text-slate-900"><span>Grand total</span><span className="tabular-nums" data-testid="so-grand-total">{inr(t.grand_total)}</span></div>
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">Order details</h3>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Delivery date</dt><dd className="text-slate-700">{so.delivery_date || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Shipping</dt><dd className="text-slate-700">{so.shipping_method || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Payment terms</dt><dd className="text-slate-700">{so.payment_terms || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Ship to</dt><dd className="text-right text-slate-700">{[so.shipping_address?.city, so.shipping_address?.state].filter(Boolean).join(", ") || "—"}</dd></div>
            </dl>
            {so.remarks && <p className="mt-3 rounded-md bg-slate-50 p-2 text-xs text-slate-600"><span className="font-medium">Remarks: </span>{so.remarks}</p>}
            {so.customer_instructions && <p className="mt-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600"><span className="font-medium">Customer: </span>{so.customer_instructions}</p>}
            {so.production_notes && <p className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-700"><span className="font-medium">Production: </span>{so.production_notes}</p>}
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900">References</h3>
            <div className="mt-3 space-y-2 text-sm">
              <button className="block text-indigo-600 hover:underline" onClick={() => navigate(`/quotations/${so.quotation_id}`)}>Quotation {so.quote_no} →</button>
              <button className="block text-indigo-600 hover:underline" onClick={() => navigate(`/customers/${so.customer_id}`)}>Customer {c.company_name} →</button>
              {so.artwork_id && <button className="block text-indigo-600 hover:underline" onClick={() => navigate(`/artwork/${so.artwork_id}`)}>Artwork {so.artwork?.artwork_code} →</button>}
              {so.enquiry_id && <button className="block text-indigo-600 hover:underline" onClick={() => navigate(`/crm/${so.enquiry_id}`)}>Enquiry {so.enquiry_no} →</button>}
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900">History</h3>
        <div className="mt-4 space-y-3">
          {[...(so.history || [])].reverse().map((h, i) => (
            <div key={i} className="flex gap-3 border-b border-slate-100 pb-3" data-testid="so-history-item">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" />
              <div><p className="text-sm text-slate-700"><span className="font-medium">{h.status}</span>{h.note ? ` · ${h.note}` : ""}</p><p className="text-xs text-slate-400">{h.by} · {new Date(h.at).toLocaleString()}</p></div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel Sales Order</DialogTitle><DialogDescription>Cancelling releases the source quotation for re-conversion. This cannot be undone.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>Reason</Label><Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} data-testid="cancel-reason-input" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setCancelOpen(false)}>Keep order</Button><Button onClick={submitCancel} data-testid="confirm-cancel-btn" className="bg-rose-600 text-white hover:bg-rose-700">Cancel order</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request a revision</DialogTitle><DialogDescription>Changes to a confirmed order require approval before they take effect.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Field to change</Label>
              <Select value={rev.field} onValueChange={(v) => setRev({ field: v, value: "", reason: rev.reason })}>
                <SelectTrigger data-testid="revision-field-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FIELD_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>New value</Label>
              {rev.field === "priority" ? (
                <Select value={rev.value} onValueChange={(v) => setRev({ ...rev, value: v })}>
                  <SelectTrigger data-testid="revision-value-input"><SelectValue placeholder="Select priority" /></SelectTrigger>
                  <SelectContent>{meta.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              ) : rev.field === "shipping_method" ? (
                <Select value={rev.value} onValueChange={(v) => setRev({ ...rev, value: v })}>
                  <SelectTrigger data-testid="revision-value-input"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>{meta.shipping_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              ) : rev.field === "delivery_date" ? (
                <Input type="date" value={rev.value} onChange={(e) => setRev({ ...rev, value: e.target.value })} data-testid="revision-value-input" />
              ) : (
                <Input value={rev.value} onChange={(e) => setRev({ ...rev, value: e.target.value })} data-testid="revision-value-input" />
              )}
            </div>
            <div className="space-y-1.5"><Label>Reason</Label><Textarea value={rev.reason} onChange={(e) => setRev({ ...rev, reason: e.target.value })} rows={2} data-testid="revision-reason-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRevOpen(false)}>Cancel</Button><Button onClick={submitRevision} data-testid="submit-revision-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Submit for approval</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
